import { useParams, useNavigate } from 'react-router-dom';
import { FormProvider, useForm } from 'react-hook-form';
import type { Resolver, UseFormSetError } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import type { AxiosError } from 'axios';
import { Skeleton } from '@/components/ui/skeleton';
import { promotionSchema, type PromotionFormValues } from '~/lib/schemas/promotion';
import { tokenToForm } from '~/lib/mappers/promotion';
import { usePromotion } from '~/hooks/use-promotion';
import { usePromotionMutations } from '~/hooks/use-promotion-mutations';
import { EditorShell } from '~/components/promotion/editor-shell';
import { PageContainer } from '~/components/page-container';

interface ServerError {
	validation?: Record<string, string>;
	msg?: string;
	title?: string;
}

/** Maps a 400 `validation: { FieldPath: message }` body onto RHF field errors (08-backend §10). */
function applyServerValidation(err: unknown, setError: UseFormSetError<PromotionFormValues>): boolean {
	const data = (err as AxiosError<ServerError>)?.response?.data;
	const validation = data?.validation;
	if (!validation || typeof validation !== 'object') return false;
	let handled = false;
	for (const [key, message] of Object.entries(validation)) {
		// Server keys are PascalCase token fields; RHF fields are camelCase (best-effort).
		const field = (key.charAt(0).toLowerCase() + key.slice(1)) as keyof PromotionFormValues;
		setError(field, { message: String(message) });
		handled = true;
	}
	return handled;
}

function serverMessage(err: unknown): string {
	const ax = err as AxiosError<ServerError>;
	return ax?.response?.data?.msg ?? ax?.response?.data?.title ?? ax?.message ?? 'Error';
}

function PromotionForm({ initial }: { initial: PromotionFormValues }) {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const { create, update } = usePromotionMutations();
	const form = useForm<PromotionFormValues>({
		resolver: zodResolver(promotionSchema) as Resolver<PromotionFormValues>,
		defaultValues: initial,
		mode: 'onChange',
		shouldUnregister: false
	});

	const onSubmit = form.handleSubmit(async values => {
		try {
			const saved = values.id ? await update.mutateAsync(values) : await create.mutateAsync(values);
			form.reset(tokenToForm(saved)); // re-hydrate (server-assigned ids) + clear dirty
			toast.success(t('common.save'));
			if (!values.id && saved.id) navigate(`/promotions/${saved.id}`, { replace: true });
		} catch (err) {
			if (!applyServerValidation(err, form.setError)) toast.error(serverMessage(err));
		}
	});

	return (
		<FormProvider {...form}>
			<form onSubmit={onSubmit} noValidate>
				<EditorShell />
			</form>
		</FormProvider>
	);
}

function EditorSkeleton() {
	return (
		<div className="flex flex-col gap-4">
			<Skeleton className="h-24 w-full rounded-lg" />
			<div className="grid grid-cols-[200px_1fr_320px] gap-4">
				<Skeleton className="h-64 rounded-lg" />
				<Skeleton className="h-96 rounded-lg" />
				<Skeleton className="h-96 rounded-lg" />
			</div>
		</div>
	);
}

export function PromotionEditor() {
	const { id } = useParams();
	const policyId = id ?? 'demo';
	const { data, isLoading, isError } = usePromotion(policyId);

	return (
		<PageContainer size="wide">
			{isLoading ? (
				<EditorSkeleton />
			) : isError || !data ? (
				<div className="rounded-lg border border-border bg-surface p-8 text-center text-sm text-foreground-muted">—</div>
			) : (
				<PromotionForm key={policyId} initial={data} />
			)}
		</PageContainer>
	);
}
